import { useState } from "react";
import { Network, Plus, X, Trash2, Wifi, Loader2 } from "lucide-react";
import { useVLANs, useCreateVLAN, useDeleteVLAN, useAttachVM, useDetachVM } from "@/hooks/useVLANs";
import { useVMs } from "@/hooks/useVMs";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import type { TenantVLAN } from "@/types/vlan";

interface VLANManagerProps {
  /** If set, only show VLANs relevant to this VM + attach/detach controls */
  vmId?: number;
}

export function VLANManager({ vmId }: VLANManagerProps) {
  const { data: vlans, isLoading } = useVLANs();
  const { data: vms } = useVMs();
  const createVLAN = useCreateVLAN();
  const deleteVLAN = useDeleteVLAN();
  const attachVM = useAttachVM();
  const detachVM = useDetachVM();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<TenantVLAN | null>(null);
  const [attachVlanId, setAttachVlanId] = useState<number | null>(null);
  const [attachVmId, setAttachVmId] = useState<number | "">("");

  const handleCreate = () => {
    if (!newName.trim()) return;
    createVLAN.mutate({ name: newName.trim() }, {
      onSuccess: () => {
        setNewName("");
        setShowCreate(false);
      },
    });
  };

  const handleDelete = (vlan: TenantVLAN) => {
    deleteVLAN.mutate(vlan.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  };

  const handleAttach = () => {
    if (attachVlanId == null || !attachVmId) return;
    attachVM.mutate(
      { vlanId: attachVlanId, vmId: Number(attachVmId) },
      { onSuccess: () => { setAttachVlanId(null); setAttachVmId(""); } },
    );
  };

  const handleDetach = (vlanId: number, targetVmId: number) => {
    detachVM.mutate({ vlanId, vmId: targetVmId });
  };

  // If vmId is set, filter VLANs to show attached ones + allow attaching to others
  const attachedVlans = vmId
    ? (vlans ?? []).filter(v => v.memberships.some(m => m.vm_id === vmId))
    : [];
  const availableVlans = vmId
    ? (vlans ?? []).filter(v => !v.memberships.some(m => m.vm_id === vmId))
    : [];

  // ── VM-scoped view (inside VMDetail) ──
  if (vmId !== undefined) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wifi className="h-5 w-5 text-cyan-400" />
            <h2 className="text-lg font-semibold text-gray-200">Networks</h2>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Create Network
          </button>
        </div>

        {/* Create network form */}
        {showCreate && (
          <div className="flex items-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3 mb-4">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Network name (e.g. Production)"
              className="flex-1 rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
            <button
              onClick={handleCreate}
              disabled={!newName.trim() || createVLAN.isPending}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              {createVLAN.isPending ? "Creating..." : "Create"}
            </button>
            <button
              onClick={() => { setShowCreate(false); setNewName(""); }}
              className="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-400 hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading networks...
          </div>
        ) : attachedVlans.length === 0 && !showCreate ? (
          <p className="text-sm text-gray-500 mb-3">Not connected to any private network.</p>
        ) : null}

        {/* Attached VLANs */}
        {!isLoading && (
        <div className="space-y-2 mb-4">
          {attachedVlans.map((vlan) => {
            const membership = vlan.memberships.find(m => m.vm_id === vmId);
            return (
              <div
                key={vlan.id}
                className="flex items-center justify-between rounded-lg border border-gray-700 bg-gray-800/50 px-4 py-2.5"
              >
                <div className="flex items-center gap-3">
                  <Network className="h-4 w-4 text-cyan-400" />
                  <span className="text-sm font-medium text-gray-200">{vlan.name}</span>
                  <span className="rounded-full bg-cyan-500/20 px-2 py-0.5 text-xs text-cyan-400">
                    VLAN {vlan.vlan_tag}
                  </span>
                  {membership && (
                    <span className="text-xs text-gray-500">{membership.interface_name}</span>
                  )}
                </div>
                <button
                  onClick={() => handleDetach(vlan.id, vmId)}
                  disabled={detachVM.isPending}
                  className="rounded p-1 text-gray-500 hover:bg-gray-700 hover:text-red-400"
                  title="Leave network"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
        )}

        {/* Join another VLAN */}
        {!isLoading && availableVlans.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={attachVlanId ?? ""}
              onChange={(e) => setAttachVlanId(e.target.value ? Number(e.target.value) : null)}
              className="flex-1 rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200"
            >
              <option value="">Join a network...</option>
              {availableVlans.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} (VLAN {v.vlan_tag})
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                if (attachVlanId != null) {
                  attachVM.mutate(
                    { vlanId: attachVlanId, vmId },
                    { onSuccess: () => setAttachVlanId(null) },
                  );
                }
              }}
              disabled={attachVlanId == null || attachVM.isPending}
              className="inline-flex items-center gap-1 rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              Join
            </button>
          </div>
        )}

        {/* Error display */}
        {(createVLAN.isError || attachVM.isError || detachVM.isError || deleteVLAN.isError) && (
          <p className="text-sm text-red-400 mt-2">
            {(createVLAN.error || attachVM.error || detachVM.error || deleteVLAN.error)?.message || "Operation failed"}
          </p>
        )}
      </div>
    );
  }

  // ── Full VLAN management view (standalone) ──
  if (isLoading) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Wifi className="h-5 w-5 text-cyan-400" />
          <h2 className="text-lg font-semibold text-gray-200">Private Networks</h2>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading networks...
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wifi className="h-5 w-5 text-cyan-400" />
          <h2 className="text-lg font-semibold text-gray-200">Private Networks</h2>
          <span className="text-xs text-gray-500">5 EUR/month per network</span>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-700"
        >
          <Plus className="h-3.5 w-3.5" />
          Create Network
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="flex items-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Network name (e.g. Production)"
            className="flex-1 rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          />
          <button
            onClick={handleCreate}
            disabled={!newName.trim() || createVLAN.isPending}
            className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
          >
            {createVLAN.isPending ? "Creating..." : "Create"}
          </button>
          <button
            onClick={() => { setShowCreate(false); setNewName(""); }}
            className="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-400 hover:bg-gray-800"
          >
            Cancel
          </button>
        </div>
      )}

      {/* VLAN list */}
      {(vlans ?? []).length === 0 ? (
        <p className="text-sm text-gray-500">No private networks created yet.</p>
      ) : (
        <div className="space-y-3">
          {(vlans ?? []).map((vlan) => (
            <div
              key={vlan.id}
              className="rounded-lg border border-gray-700 bg-gray-800/30 p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Network className="h-4 w-4 text-cyan-400" />
                  <span className="font-medium text-gray-200">{vlan.name}</span>
                  <span className="rounded-full bg-cyan-500/20 px-2 py-0.5 text-xs text-cyan-400">
                    VLAN {vlan.vlan_tag}
                  </span>
                  <span className="text-xs text-gray-500">
                    {vlan.memberships.length} VM{vlan.memberships.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono text-gray-400">{vlan.monthly_cost.toFixed(2)} EUR/mo</span>
                  <button
                    onClick={() => setDeleteTarget(vlan)}
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-700 hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Members */}
              {vlan.memberships.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {vlan.memberships.map((m) => {
                    const vm = (vms ?? []).find(v => v.id === m.vm_id);
                    return (
                      <span
                        key={m.id}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-700 px-2.5 py-1 text-xs text-gray-300"
                      >
                        {vm?.name ?? `VM #${m.vm_id}`}
                        <span className="text-gray-500">({m.interface_name})</span>
                        <button
                          onClick={() => handleDetach(vlan.id, m.vm_id)}
                          className="ml-0.5 rounded-full p-0.5 hover:bg-gray-600 hover:text-red-400"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Attach VM */}
              {attachVlanId === vlan.id ? (
                <div className="flex items-center gap-2 mt-2">
                  <select
                    value={attachVmId}
                    onChange={(e) => setAttachVmId(e.target.value ? Number(e.target.value) : "")}
                    className="flex-1 rounded-lg border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm text-gray-200"
                  >
                    <option value="">Select a VM...</option>
                    {(vms ?? [])
                      .filter(v => !vlan.memberships.some(m => m.vm_id === v.id))
                      .map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))
                    }
                  </select>
                  <button
                    onClick={handleAttach}
                    disabled={!attachVmId || attachVM.isPending}
                    className="rounded-lg bg-cyan-600 px-3 py-1.5 text-sm text-white hover:bg-cyan-700 disabled:opacity-50"
                  >
                    Attach
                  </button>
                  <button
                    onClick={() => { setAttachVlanId(null); setAttachVmId(""); }}
                    className="text-sm text-gray-500 hover:text-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setAttachVlanId(vlan.id)}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                >
                  <Plus className="h-3 w-3" />
                  Add VM
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Error display */}
      {(createVLAN.isError || attachVM.isError || detachVM.isError || deleteVLAN.isError) && (
        <p className="text-sm text-red-400 mt-2">
          {(createVLAN.error || attachVM.error || detachVM.error || deleteVLAN.error)?.message || "Operation failed"}
        </p>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <ConfirmDialog
          open={true}
          title={`Delete network "${deleteTarget.name}"?`}
          message={`This will remove VLAN ${deleteTarget.vlan_tag} and detach all ${deleteTarget.memberships.length} connected VMs. The 5 EUR/month charge will be removed.`}
          onConfirm={() => handleDelete(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          loading={deleteVLAN.isPending}
          variant="danger"
        />
      )}
    </div>
  );
}
